import { useMemo } from "react";

import { Box, Chip, Stack, Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { EMPTY_VALUE_PLACEHOLDER, formatShortDate, formatTime } from "@shared";

import { EllipsisText } from "@/common/components/ellipsis-text/EllipsisText";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useTranslation } from "@/translations";

import { ChangeHistoryRow } from "../ChangeHistory.utils";

/** One line of text in a cell, centred in the row. */
function renderText(value: string | null) {
  return value == null ? (
    EMPTY_VALUE_PLACEHOLDER
  ) : (
    <Stack justifyContent="center" sx={{ height: "100%", width: "100%", minWidth: 0 }}>
      <EllipsisText value={value} variant="body2" />
    </Stack>
  );
}

/**
 * The changed field of the entry a row belongs to, or null where there is no detail to show — a row
 * without an entry (its field is the subject of the change and sits in "Feld") and an added/removed
 * entry, which names no field. Whether to show the column and what to put in it is one question.
 */
function detailOf(row: ChangeHistoryRow): string | null {
  return row.entryLabel == null ? null : row.field;
}

/**
 * Column definitions for the change-history table. Read-only — mirrors the takeover history table.
 * <p>
 * "Feld" holds what the change is about: the entry it belongs to or, where there is none, the changed
 * field itself. "Detail" then names that entry's changed field, and is dropped whenever no row has
 * one — a scoped history, a section of only the client's own fields, or entries never edited.
 * </p>
 */
export function useChangeHistoryColumns(rows: ChangeHistoryRow[]): Array<GridColDef<ChangeHistoryRow>> {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const showDetailColumn = rows.some((row) => detailOf(row) != null);

  return useMemo<Array<GridColDef<ChangeHistoryRow>>>(
    () => [
      {
        field: "changedAtUtc",
        headerName: t("changeHistory.columns.timestamp"),
        type: "dateTime",
        flex: 1,
        minWidth: 120,
        renderCell: (params: GridRenderCellParams<ChangeHistoryRow>) => (
          <Stack justifyContent="center" sx={{ height: "100%" }}>
            <Typography variant="body2">{formatShortDate(params.row.changedAtUtc, locales[language])}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatTime(params.row.changedAtUtc, locales[language])} {t("common.clock")}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "kindLabel",
        headerName: t("changeHistory.columns.change"),
        flex: 1,
        // Wide enough for the longest chip label ("Eintrag hinzugefügt"); maxWidth alone only caps
        // the column, it reserves nothing, so without a minWidth the flex share truncates the chip.
        minWidth: 180,
        maxWidth: 200,
        sortable: false,
        renderCell: (params: GridRenderCellParams<ChangeHistoryRow>) => (
          <Chip label={params.row.kindLabel} size="small" color="primary" variant="outlined" />
        ),
      },
      {
        field: "entryLabel",
        // The widest column: it carries entry names and the creation texts ("Vermögensverzeichnis
        // angelegt"), while "Detail" only ever holds a field name.
        headerName: t("changeHistory.columns.field"),
        flex: 2,
        minWidth: 150,
        sortable: false,
        renderCell: (params: GridRenderCellParams<ChangeHistoryRow>) =>
          renderText(params.row.entryLabel ?? params.row.field),
      },
      ...(showDetailColumn
        ? [
            {
              field: "field",
              headerName: t("changeHistory.columns.detail"),
              flex: 1,
              minWidth: 140,
              sortable: false,
              renderCell: (params: GridRenderCellParams<ChangeHistoryRow>) => renderText(detailOf(params.row)),
            } satisfies GridColDef<ChangeHistoryRow>,
          ]
        : []),
      {
        field: "oldValue",
        headerName: t("changeHistory.columns.oldValue"),
        flex: 1.5,
        minWidth: 140,
        sortable: false,
        renderCell: (params: GridRenderCellParams<ChangeHistoryRow>) =>
          params.row.oldValue == null ? (
            EMPTY_VALUE_PLACEHOLDER
          ) : (
            <Stack justifyContent="center" sx={{ height: "100%", width: "100%", minWidth: 0 }}>
              <EllipsisText value={params.row.oldValue} variant="body2" color="text.secondary">
                <Box component="span" sx={{ textDecoration: "line-through" }}>
                  {params.row.oldValue}
                </Box>
              </EllipsisText>
            </Stack>
          ),
      },
      {
        field: "newValue",
        headerName: t("changeHistory.columns.newValue"),
        flex: 1.5,
        minWidth: 140,
        sortable: false,
        renderCell: (params: GridRenderCellParams<ChangeHistoryRow>) => renderText(params.row.newValue),
      },
      {
        field: "user",
        headerName: t("changeHistory.columns.user"),
        flex: 1,
        maxWidth: 150,
        sortable: false,
        renderCell: (params: GridRenderCellParams<ChangeHistoryRow>) => renderText(params.row.user),
      },
    ],
    [t, language, showDetailColumn]
  );
}
